import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { toastConfig } from 'assets/data/config';
import config from '../../../constants/apiConfig';
import { selectUserGuid } from 'slices/commonSelectors';
import { useActualIncidentId } from './commonHooks';
import type { Ics215Signature } from '../planningView/widgets/Ics215Table/ics215SignatureSlots';

export type Ics215ResourceListItem = {
  resourceName: string;
  required: string;
  have: string;
  need: string;
};

export type Ics215AssignmentSnapshot = {
  workAssignmentId: string;
  workAssignment: string;
  workGroupName: string;
  overheadPositions: string;
  specialEquipmentSupplies: string;
  reportingLocation: string;
  requestedArrivalTime: string | null;
  specialInstructions: string;
  resourceList: Ics215ResourceListItem[];
};

export type Ics215Payload = {
  assignments: Ics215AssignmentSnapshot[];
  resourceKinds: string[];
};

export type Ics215Tip = {
  id: string;
  ics215Id: string;
  incidentId: string;
  operationalPeriodNumber: number;
  payload: Ics215Payload;
  signatures: Ics215Signature[];
  createdAt: string;
  updatedAt: string;
  updatedByItemId?: string | null;
  updatedByUserName?: string | null;
};

export type Ics215Version = Ics215Tip;

const tipKey = (incidentId: string, periodNumber: number) =>
  ['ics215Tip', incidentId, periodNumber] as const;
const existingTipKey = (incidentId: string, periodNumber: number) =>
  ['ics215ExistingTip', incidentId, periodNumber] as const;
const versionsKey = (incidentId: string, ics215Id: string) =>
  ['ics215Versions', incidentId, ics215Id] as const;

const invalidateIcs215 = (
  queryClient: ReturnType<typeof useQueryClient>,
  incidentId: string,
  periodNumber: number,
  ics215Id?: string
) => {
  queryClient.invalidateQueries({ queryKey: tipKey(incidentId, periodNumber) });
  queryClient.invalidateQueries({
    queryKey: existingTipKey(incidentId, periodNumber),
  });
  if (ics215Id) {
    queryClient.invalidateQueries({
      queryKey: versionsKey(incidentId, ics215Id),
    });
  }
};

export const useIcs215Tip = (periodNumber: number) => {
  const incidentId = useActualIncidentId();
  return useQuery<Ics215Tip | null>({
    queryKey: tipKey(incidentId ?? '', periodNumber),
    queryFn: async () => {
      if (!incidentId) return null;
      const { data } = await axios.get(
        `${config.apiGateway.ics_215}/${incidentId}/period/${periodNumber}/tip`
      );
      return data;
    },
    enabled: !!incidentId && periodNumber >= 0,
  });
};

/** Existing 215 tip, or null. Does not create an empty draft. */
export const useIcs215ExistingTip = (periodNumber: number) => {
  const incidentId = useActualIncidentId();
  return useQuery<Ics215Tip | null>({
    queryKey: existingTipKey(incidentId ?? '', periodNumber),
    queryFn: async () => {
      if (!incidentId) return null;
      const { data } = await axios.get(
        `${config.apiGateway.ics_215}/${incidentId}/period/${periodNumber}/existing-tip`
      );
      return data;
    },
    enabled: !!incidentId && periodNumber >= 0,
  });
};

export const useIcs215Versions = (ics215Id: string) => {
  const incidentId = useActualIncidentId();
  return useQuery<Ics215Version[]>({
    queryKey: versionsKey(incidentId ?? '', ics215Id),
    queryFn: async () => {
      if (!incidentId || !ics215Id) return [];
      const { data } = await axios.get(
        `${config.apiGateway.ics_215}/${incidentId}/${ics215Id}/versions`
      );
      return data;
    },
    enabled: !!incidentId && !!ics215Id,
  });
};

export const useCreateSignedIcs215Version = (periodNumber: number) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (preparedBy: Ics215Signature) => {
      return await axios.post(
        `${config.apiGateway.ics_215}/${incidentId}/period/${periodNumber}/versions/signed`,
        { preparedBy }
      );
    },
    onSuccess: (response) => {
      const ics215Id = response?.data?.ics215Id as string | undefined;
      invalidateIcs215(queryClient, incidentId, periodNumber, ics215Id);
      toast.success('Signed ICS-215 version created.', toastConfig);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message || error.message,
        toastConfig
      );
    },
  });
};

export const useCreateUnsignedIcs215Version = (periodNumber: number) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await axios.post(
        `${config.apiGateway.ics_215}/${incidentId}/period/${periodNumber}/versions/unsigned`
      );
    },
    onSuccess: (response) => {
      const ics215Id = response?.data?.ics215Id as string | undefined;
      invalidateIcs215(queryClient, incidentId, periodNumber, ics215Id);
      toast.success('Draft ICS-215 version created.', toastConfig);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message || error.message,
        toastConfig
      );
    },
  });
};

export type Ics215ExportResult = {
  docxBase64: string;
  pages?: unknown[];
};

export const useExportIcs215 = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  void user_guid;
  return useMutation({
    mutationFn: async ({
      ics215Id,
      skipWorkspaceUpload,
      includePreviewPages,
    }: {
      ics215Id: string;
      skipWorkspaceUpload?: boolean;
      includePreviewPages?: boolean;
    }): Promise<Ics215ExportResult | string> => {
      const { data } = await axios.post(
        `${config.apiGateway.ics_215}/${incidentId}/${ics215Id}/export`,
        { skipWorkspaceUpload, includePreviewPages }
      );
      return data;
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message || error.message,
        toastConfig
      );
    },
  });
};
